import { useState, useCallback } from 'react';

/**
 * Custom hook to manage the help panel state
 * 
 * @param {Object} initialConfig - The initial configuration for the help panel
 * @param {string} initialConfig.title - The initial title of the help panel
 * @param {Object|string|React.ReactNode} initialConfig.content - The initial content of the help panel
 * @param {Array} initialConfig.sections - The initial sections of the help panel
 * @param {string} initialConfig.position - The initial position of the help panel
 * @param {string} initialConfig.size - The initial size of the help panel
 * @returns {Object} The help panel state and helper functions
 */
const useHelpPanel = (initialConfig = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState({
    title: initialConfig.title || "Help & Information",
    content: initialConfig.content || null,
    sections: initialConfig.sections || [],
    position: initialConfig.position || "right",
    size: initialConfig.size || "medium",
  });

  const open = useCallback((newConfig = {}) => {
    setConfig(prev => ({
      ...prev,
      title: newConfig.title !== undefined ? newConfig.title : prev.title,
      content: newConfig.content !== undefined ? newConfig.content : prev.content,
      sections: newConfig.sections !== undefined ? newConfig.sections : prev.sections,
      position: newConfig.position !== undefined ? newConfig.position : prev.position,
      size: newConfig.size !== undefined ? newConfig.size : prev.size,
    }));
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const update = useCallback((newConfig) => {
    setConfig(prev => ({
      ...prev,
      ...newConfig,
    }));
  }, []);

  return {
    isOpen,
    config,
    open,
    close,
    update,
  };
};

export default useHelpPanel; 